import { useQuery } from "@tanstack/react-query";
import { Navigate, Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import api from "@/lib/api";
import { useAuth } from "@/context/AuthContext";
import { useLoc } from "@/lib/loc";
import VideoCard from "@/components/VideoCard";
import Seo from "@/components/Seo";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { Loader2, History, ListVideo, Sparkles } from "lucide-react";

function Grid({ videos, empty }) {
  if (!videos?.length) return <p className="py-12 text-muted-foreground">{empty}</p>;
  return (
    <div className="grid grid-cols-2 gap-4 sm:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5">
      {videos.map((v) => (
        <VideoCard key={v.id} video={v} className="w-full" />
      ))}
    </div>
  );
}

export default function Account() {
  const { t } = useTranslation();
  const { user, ready } = useAuth();

  const history = useQuery({ queryKey: ["history"], queryFn: async () => (await api.get("/account/history")).data, enabled: !!user });
  const mylist = useQuery({ queryKey: ["mylist"], queryFn: async () => (await api.get("/account/my-list")).data, enabled: !!user });
  const recs = useQuery({ queryKey: ["recs"], queryFn: async () => (await api.get("/account/recommendations")).data, enabled: !!user });

  if (!ready) return <div className="flex h-screen items-center justify-center"><Loader2 className="h-8 w-8 animate-spin text-primary" /></div>;
  if (!user) return <Navigate to="/login" replace />;

  const historyVideos = (history.data || []).map((h) => ({ ...h.video, _resume: h.position }));

  return (
    <div className="min-h-screen px-6 pt-24 md:px-12">
      <Seo title={t("account.title")} />
      <h1 className="font-heading text-3xl font-bold tracking-tight md:text-4xl">
        {t("account.title")} · <span className="text-primary">{user.name}</span>
      </h1>

      <Tabs defaultValue="history" className="mt-6">
        <TabsList className="bg-white/5">
          <TabsTrigger value="history" data-testid="tab-history"><History className="mr-2 h-4 w-4" />{t("account.history")}</TabsTrigger>
          <TabsTrigger value="mylist" data-testid="tab-mylist"><ListVideo className="mr-2 h-4 w-4" />{t("account.mylist")}</TabsTrigger>
          <TabsTrigger value="recs" data-testid="tab-recs"><Sparkles className="mr-2 h-4 w-4" />{t("account.recommendations")}</TabsTrigger>
        </TabsList>

        <TabsContent value="history" className="mt-6 pb-16">
          <Grid videos={historyVideos} empty={t("account.emptyHistory")} />
        </TabsContent>
        <TabsContent value="mylist" className="mt-6 pb-16">
          <Grid videos={mylist.data} empty={t("account.emptyList")} />
        </TabsContent>
        <TabsContent value="recs" className="mt-6 pb-16">
          <Grid videos={recs.data} empty={t("common.noResults")} />
        </TabsContent>
      </Tabs>
    </div>
  );
}
